import React, { useEffect } from 'react'
import { FormSection } from '@/common/components/Form/FormSection.tsx'
import { FormMoneyInput } from '@/common/components/Form/FormMoneyInput.tsx'
import { FormNumberInput2 } from '@/common/components/Form/FormNumberInput2.tsx'
import { FormInput } from '@/common/components/Form/FormInput.tsx'
import { z } from 'zod'
import { Money, Percent } from '@/common/types/misc.def.ts'
import { useFormContext } from 'react-hook-form'
import { FormSelectAntd } from '@/common/components/Form/FormSelectAntd.tsx'
import { FormSwitch } from '@/common/components/Form/FormSwitch.tsx'
import { FormTextArea } from '@/common/components/Form/FormTextArea.tsx'
import { Currency } from '@/common/types/region-config.def.ts'

export type RiskParametersBuildArgs = {
  needCn?: boolean
  needTermSheetExtra?: boolean
  needHasGmv?: boolean
  needHasIntellectualProperty?: boolean
  needBuyingPowerRequireApproval?: boolean
}

export const buildRiskParametersSchema = (args?: RiskParametersBuildArgs) => {
  return z.object({
    buyingPower: Money,
    buyingPowerRequireManagementApproval: z.coerce.number().optional(),
    netLimitRate: Percent,
    netLimitType: z.string(),
    netLimitTypeOther: z.string().optional(),
    netLimitRate2: Percent.optional(),
    netLimitType2: z.string().optional(),
    netLimitTypeOther2: z.string().optional(),
    maximumPositionRate: Percent,
    maximumPositionType: z.string(),
    maximumPositionTypeOther: z.string().optional(),
    cnMaximumPositionType: z.string().optional(),
    marketCapitalizationLimitM: z.coerce.number(),
    liquidityLimitRate1: Percent,
    liquidityLimitRate2: Percent.optional(),
    liquidityLimitDays1: z.coerce.number().min(0),
    liquidityLimitDays2: z.coerce.number().min(0).optional(),
    speedBumpBpLimitRate: Percent.optional(),
    specialNotes: z.string().optional(),
    downAndOutBpRate: Percent,
    countryLimits: z.string().optional(),
    cnCountryLimits: z.string().optional(),
    // extra parameters for term sheet
    speedBumpBpCutRate: Percent.optional(),
    mandateDetails: z.string().min(1),
    mandate: z.string().min(1),
    cnMandateDetails: z.string().min(1).optional(),
    cnMandate: z.string().min(1).optional(),
    hasGmv: z.boolean().optional(),
    gmvLimits1: z.coerce.number().min(0).optional(),
    gmvLimits2: z.coerce.number().min(0).optional(),
    hasIntellectualProperty: z.boolean().optional(),
  }).superRefine((data, ctx) => {

    if (args?.needTermSheetExtra && data.speedBumpBpLimitRate) {
      if (!data.speedBumpBpCutRate) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Required', path: ['speedBumpBpCutRate'] })
      }
    }

    if (data.liquidityLimitRate2 && !data.liquidityLimitDays2) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Required', path: ['liquidityLimitDays2'] })
    }

    if (data.netLimitType === 'Other' && !data.netLimitTypeOther) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Required', path: ['netLimitTypeOther'] })
    }
  })
}

export const RiskParametersSection: React.FC<RiskParametersBuildArgs> = ({
  needCn,
  needTermSheetExtra,
  needHasGmv,
  needHasIntellectualProperty,
}) => {
  const { watch, resetField, setError } = useFormContext()

  const speedBumpBpLimitRate = parseFloat(watch('riskParameters.speedBumpBpLimitRate'))
  useEffect(() => {
    if (!speedBumpBpLimitRate) {
      resetField('riskParameters.speedBumpBpCutRate')
    }
  }, [resetField, speedBumpBpLimitRate])

  const liquidityLimitRate2 = parseFloat(watch('riskParameters.liquidityLimitRate2'))
  useEffect(() => {
    if (!liquidityLimitRate2) {
      resetField('riskParameters.liquidityLimitDays2')
    }
  }, [resetField, liquidityLimitRate2])

  const netLimitType = watch('riskParameters.netLimitType')
  const netLimitType2 = watch('riskParameters.netLimitType2')

  const maximumPositionType = watch('riskParameters.maximumPositionType')

  const speedBumpBPLimitRate = watch('riskParameters.speedBumpBpLimitRate')

  useEffect(() => {
    setError('riskParameters.speedBumpBpCutRate', { message: 'Required', type: 'required' })

  }, [speedBumpBPLimitRate])

  useEffect(() => {
    if (netLimitType === 'Other') {
      setError('riskParameters.netLimitTypeOther', { message: 'Required', type: 'required' })
    }
  }, [netLimitType])

  return (
    <FormSection name="riskParameters">
      <FormMoneyInput name="buyingPower" label="Buying Power ($MM)" currencyOptions={[Currency.USD]} />
      <FormNumberInput2 name="buyingPowerRequireManagementApproval" label="GMV value - reguire mgmt. approval ($MM)" />
      <FormNumberInput2 name="netLimitRate" variant="percent" />
      <FormSelectAntd
        name="netLimitType"
        options={[{
          label: 'Gross market value',
          value: 'Gross Market Value',
        }, { label: 'Other (Please Input)', value: 'Other' }]}
      />
      {netLimitType == 'Other' && <FormInput name="netLimitTypeOther" />}
      <FormNumberInput2 name="netLimitRate2" variant="percent" />
      <FormSelectAntd
        name="netLimitType2"
        options={[{
          label: 'Gross market value',
          value: 'Gross Market Value',
        }, { label: 'Other (Please Input)', value: 'Other' }]}
      />
      {netLimitType2 == 'Other' && <FormInput name="netLimitTypeOther2" />}
      <FormNumberInput2 name="maximumPositionRate" variant="percent" />
      <FormSelectAntd
        name="maximumPositionType"
        label="Maximum Position Rate % of"
        options={[{
          label: 'Notional/Buying Power',
          value: 'Notional/Buying Power',
        }, { label: 'Gross market value', value: 'Gross market value' }, {
          label: 'Other (Please Input)',
          value: 'Other',
        }]}
      />
      {maximumPositionType == 'Other' && <FormInput name="maximumPositionTypeOther" />}
      {needHasGmv && <FormNumberInput2
        name="gmvLimits1"
        label="GMV limits example 1"
        description="In USD million"
        placeholder="Enter Buying Power"
      />}
      {needHasGmv && <FormNumberInput2
        name="gmvLimits2"
        label="GMV limits example 2"
        description="In USD million"
        placeholder="Enter Buying Power"
      />}
      <FormNumberInput2
        name="marketCapitalizationLimitM"
        label="Market Capitalization Limit ($MM)"
        description="In USD million"
      />
      {(!needCn && !needTermSheetExtra) && <div></div>}
      <FormNumberInput2 name="liquidityLimitRate1" variant="percent" />
      <FormNumberInput2 name="liquidityLimitDays1" />
      <FormNumberInput2 name="liquidityLimitRate2" variant="percent" description="Put zero if not applicable" />
      <FormNumberInput2 name="liquidityLimitDays2" disabled={!liquidityLimitRate2} />
      <FormNumberInput2
        name="speedBumpBpLimitRate"
        label="Speed Bump BP Limit Rate"
        variant="percent"
        description="Put zero if not applicable"
      />
      <FormNumberInput2
        name="speedBumpBpCutRate"
        label="Speed Bump BP Cut Rate"
        variant="percent"
        disabled={!speedBumpBpLimitRate}
      />
      <FormTextArea name="specialNotes" label="Speed Bump Special Notes" />
      <FormNumberInput2 name="downAndOutBpRate" label="Down and Out BP Rate" variant="percent" />
      <FormInput name="mandate" />
      {needCn && <FormInput name="cnMandate" />}
      <FormInput name="mandateDetails" />
      {needCn && <FormInput name="cnMandateDetails" />}
      <FormInput name="countryLimits" placeholder="No investing in ... names" />
      {needCn && <FormInput name="cnCountryLimits" />}
      {needHasIntellectualProperty && <FormSwitch name="hasIntellectualProperty" label="Has Intellectual Property" />}
    </FormSection>
  )
}